/* CreateQuestionRenderer.js */

"use strict";
const uiCore = require("@gluenetworks/steve_ui_core");
const ButtonControl = uiCore.Control.Action.Button;
const InputTextControl = uiCore.Control.Textfield.InputText;
const ComboBoxControl = uiCore.Control.OptionSelection.Single.ComboBox;
const _ = require("lodash");
const codeNameRegExpStr = "[A-Z0-9_.\\-]+";
const codeNameRegExp = new RegExp("^" + codeNameRegExpStr + "$");
class CreateQuestionRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("base-create-gl");
        this._rootElem.component = this._model;
        this._paths = this._model._paths || [];

        this._nameField = new InputTextControl({
            darkTheme: true,
            placeholder: "Enter Site Name",
        });

        this._codeField = new InputTextControl({
            darkTheme: true,
            placeholder: "Enter Code Name",
            patternMatchAll: true,
            pattern: codeNameRegExpStr,
            showErrorMessage: true,
            validationVerbiage: {
                pattern: "Please enter a value matching pattern: " + codeNameRegExpStr,
            },
        });

        let defaultPathIndex = this._paths.findIndex((path) => path.label === "/");
        if (defaultPathIndex === -1 && this._paths.length > 0) {
            defaultPathIndex = 0;
        }
        this._path = new ComboBoxControl({
            showFilter: true,
            adjustWidth: true,
            darkTheme: true,
            options: this._model._paths || [],
            value: defaultPathIndex > -1 ? this._paths[defaultPathIndex].value : null,
        });

        const pathLabel = document.createElement("label");
        pathLabel.innerText = "Path";
        pathLabel.title = "Select Path for Site";

        this._confirmButton = new ButtonControl({
            label: "Create",
            darkTheme: true,
            theme: "additive",
            disabled: true,
        });

        this._cancelButton = new ButtonControl({
            label: "Cancel",
            darkTheme: true,
        });
        this._titleComponent = document.createElement("h2");
        this._titleComponent.innerText = this._model.titleText;

        this._formContainer = document.createElement("div");
        this._formContainer.classList.add("form-container");

        this._buttonContainer = document.createElement("button-container");

        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._confirmButton.on(
            "UserClick",
            (this._onConfirmButtonClick = this._onConfirmButtonClick.bind(this))
        );
        this._cancelButton.on(
            "UserClick",
            (this._onCancelButtonClick = this._onCancelButtonClick.bind(this))
        );
        this._nameField.on(
            "UserKeyDown",
            (this._checkForImpliedConfirm = this._checkForImpliedConfirm.bind(this))
        );
        this._nameField.on("UserValue", (this.checkValue = this.checkValue.bind(this)));
        this._codeField.on("UserValue", (this.checkValue = this.checkValue.bind(this)));
        // this._path.on("UserValue", (this.checkValue = this.checkValue.bind(this)));
    }

    _unregisterListeners() {
        this._confirmButton.removeAllListeners("UserClick");
        this._nameField.removeAllListeners("UserKeyDown");
        this._nameField.removeAllListeners("UserValue");
        this._codeField.removeAllListeners("UserValue");
        // this._path.removeAllListeners("UserValue");
        this._cancelButton.removeAllListeners("UserClick");
    }

    _onConfirmButtonClick() {
        this._callCreateAction();
    }
    _onCancelButtonClick() {
        this._model.cancelAction();
    }
    _checkForImpliedConfirm(event) {
        if (event.keyCode === 13) {
            if (!_.isEmpty(this._nameField.getValue())) {
                this._callCreateAction();
            }
        }
    }
    checkValue() {
        this._confirmButton.disabled = !this.isValid();
    }

    isValid() {
        const name = this._nameField.getValue();
        const code = this._codeField.getValue();
        let isCodeValid = false;
        let isNameValid = false;
        if (name && name.trim().length > 0) {
            isNameValid = true;
        }
        if (code && code.trim().length > 0 && codeNameRegExp.test(code)) {
            isCodeValid = true;
        }
        return isNameValid && isCodeValid;
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        let promArr = [
            this._nameField.shutdown(),
            this._codeField.shutdown(),
            this._path.shutdown(),
            this._confirmButton.shutdown(),
            this._cancelButton.shutdown(),
        ];

        return Promise.all(promArr).then(
            () => {
                this._nameField = undefined;
                this._codeField = undefined;
                this._path = undefined;
                this._confirmButton = undefined;
                this._buttonContainer.remove();
                this._buttonContainer = undefined;
                this._titleComponent.remove();
                this._titleComponent = undefined;
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
                this._cancelButton = undefined;
            },
            (err) => {
                this._model.logger.warn(err, "CreateQuestionRenderer.shutdown() error");
            }
        );
    }
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return new Promise((resolve /*,reject*/) => {
            if (!this._rootElem.hasChildNodes()) {
                this._rootElem.appendChild(this._titleComponent);
                this._rootElem.appendChild(this._formContainer);
                if (this._model._paths) {
                    this._formContainer.appendChild(
                        this.wrapElement(this._path.renderer.rootElement, "Path", "input-container")
                    );
                }

                this._formContainer.appendChild(
                    this.wrapElement(
                        this._nameField.renderer.rootElement,
                        "Name",
                        "input-container"
                    )
                );
                this._formContainer.appendChild(
                    this.wrapElement(
                        this._codeField.renderer.rootElement,
                        "Code Name",
                        "input-container"
                    )
                );

                this._buttonContainer.appendChild(this._cancelButton.renderer.rootElement);
                this._buttonContainer.appendChild(this._confirmButton.renderer.rootElement);
                this._rootElem.appendChild(this._buttonContainer);
                setTimeout(() => {
                    // this._path.setValue("");
                    this._path.setValue(this._model._selectedSite);
                    if (this._model._value) {
                        this._nameField.setValue(this._model._value);
                        this.checkValue(this._nameField.getValue());
                    }
                    this._nameField.focus();
                });
            }
            resolve();
        });
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/
    //TODO Private Render Methods here
    /*******************************************/

    wrapElement(elem, label, className) {
        const el = document.createElement("div");
        el.classList.add(className);
        el.innerHTML = `
        <label>${label}: <label>
        `;
        el.appendChild(elem);
        return el;
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _callCreateAction() {
        let data = {};
        data.name = this._nameField.getValue().trim();
        data.code = this._codeField.getValue().trim();
        data.parentSite = this._path.getValue();
        this._model.createAction(data);
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}
module.exports = CreateQuestionRenderer;
